import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../lib/toasts';
import {
  AssistantRequestError, fetchConversation, fetchTurn, newIdempotencyKey, startConversation, startTurn, undoAction,
  type AssistantAction, type AssistantConversationDetail, type AssistantTurn,
} from './assistant-api';
import { actionSummary } from './action-text';
import { isTerminal, TURN_MAX_POLLS, TURN_POLL_MS, turnPollInterval } from './poll';
import type { AssistantContext } from './route-context';

export type ChatOutcome = 'success' | 'capped' | 'failed' | 'budget' | 'timeout';
/** One question and what came of it. */
export interface ChatTurn {
  readonly id: string;
  readonly text: string;
  readonly answer: string | null;
  readonly actions: readonly AssistantAction[];
  readonly credits: string | null;
  readonly suggestions: readonly string[];
  readonly outcome: ChatOutcome;
}
export type SendError = 'lost' | 'offline' | 'budget' | 'limit' | 'dayLimit' | 'inProgress' | 'closed' | 'generic';

function classify(error: unknown): SendError {
  if (!(error instanceof AssistantRequestError)) return 'generic';
  if (error.status === 0) return 'offline';
  if (error.status === 422 && (error.problem === 'ai-budget-exhausted' || error.problem === 'ai-budget-not-set')) return 'budget';
  if (error.problem === 'assistant-conversation-limit') return 'limit';
  if (error.problem === 'assistant-day-limit') return 'dayLimit';
  if (error.problem === 'assistant-turn-in-progress') return 'inProgress';
  if (error.problem === 'assistant-conversation-closed') return 'closed';
  return 'generic';
}

function outcomeOf(turn: AssistantTurn): ChatOutcome {
  if (turn.status === 'rejected_budget' || turn.error_code === 'budget_exhausted') return 'budget';
  if (turn.error_code === 'turn_timeout') return 'timeout';
  if (turn.status === 'failed') return 'failed';
  return turn.error_code === 'turn_capped' ? 'capped' : 'success';
}

/** The turns of a stored conversation: the member's question, the answer, the actions of that turn. */
export function turnsOf(detail: AssistantConversationDetail): ChatTurn[] {
  const order: string[] = [];
  const byTurn = new Map<string, { text: string; answer: string | null; suggestions: string[] }>();
  for (const message of [...detail.messages].sort((a, b) => a.seq - b.seq)) {
    const entry = byTurn.get(message.turn_id) ?? { text: '', answer: null, suggestions: [] };
    if (!byTurn.has(message.turn_id)) order.push(message.turn_id);
    if (message.role === 'user') entry.text = message.text;
    else {
      entry.answer = message.text;
      entry.suggestions = message.suggestions;
    }
    byTurn.set(message.turn_id, entry);
  }
  return order.map((id) => {
    const entry = byTurn.get(id)!;
    return { id, text: entry.text, answer: entry.answer, actions: detail.actions.filter((a) => a.turn_id === id), credits: null, suggestions: entry.suggestions, outcome: 'success' };
  });
}

/**
 * The conversation of the panel (spec 110 section 4.15): sends a message (a new conversation or a new
 * turn), polls the running turn with a cap, keeps the finished turns, and runs undo per action. All
 * failures end in a visible state: a `SendError` for a message that did not start, an outcome for a
 * turn that did, a toast for an undo.
 */
export function useAssistantChat(context: AssistantContext) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [active, setActive] = useState<{ id: string; text: string } | null>(null);
  const [sendError, setSendError] = useState<SendError | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, AssistantAction>>({});
  const [conflicts, setConflicts] = useState<ReadonlySet<string>>(new Set());
  const [undoing, setUndoing] = useState<ReadonlySet<string>>(new Set());
  const settled = useRef<string | null>(null);
  /** Bumped by every reset, open and send: a history response that arrives for an older one is dropped. */
  const generation = useRef(0);

  const turnKey = ['assistant', 'turn', conversationId, active?.id] as const;
  const poll = useQuery({
    queryKey: turnKey,
    // The poll count travels with the recorded answer, so the interval and the cap see the same number.
    queryFn: async () => {
      const before = queryClient.getQueryData<{ polls: number }>(turnKey)?.polls ?? 0;
      return { turn: await fetchTurn(conversationId!, active!.id), polls: before + 1 };
    },
    enabled: conversationId !== null && active !== null,
    refetchInterval: (query) => turnPollInterval(query.state.data?.turn.status, query.state.data?.polls ?? 0, { intervalMs: TURN_POLL_MS, maxPolls: TURN_MAX_POLLS }),
    gcTime: 0,
    retry: 2,
  });

  const finish = useCallback((turn: ChatTurn) => {
    setTurns((current) => [...current, turn]);
    setActive(null);
  }, []);

  useEffect(() => {
    if (!active || settled.current === active.id) return;
    const data = poll.data?.turn;
    if (data && isTerminal(data.status)) {
      settled.current = active.id;
      finish({ id: active.id, text: active.text, answer: data.answer, actions: data.actions, credits: data.credits, suggestions: data.suggestions, outcome: outcomeOf(data) });
    } else if (data && (poll.data?.polls ?? 0) >= TURN_MAX_POLLS) {
      settled.current = active.id;
      finish({ id: active.id, text: active.text, answer: null, actions: [], credits: null, suggestions: [], outcome: 'timeout' });
    } else if (poll.isError) {
      settled.current = active.id;
      setSendError('lost');
      setActive(null);
    }
  }, [active, poll.data, poll.isError, finish]);

  const send = useCallback(
    async (rawText: string): Promise<boolean> => {
      const text = rawText.trim();
      if (!text || active || loading) return false;
      setSendError(null);
      const mine = ++generation.current;
      const key = newIdempotencyKey();
      try {
        let turnId: string;
        if (conversationId) turnId = (await startTurn(conversationId, text, context, key)).id;
        else {
          const started = await startConversation(text, context, key);
          if (generation.current !== mine) return false;
          setConversationId(started.conversation.id);
          turnId = started.turn.id;
        }
        if (generation.current !== mine) return false;
        setActive({ id: turnId, text });
        return true;
      } catch (error) {
        const kind = classify(error);
        if (error instanceof AssistantRequestError && error.problem === 'assistant-disabled') void queryClient.invalidateQueries({ queryKey: ['assistant', 'availability'] });
        setSendError(kind);
        return false;
      }
    },
    [active, loading, conversationId, context, queryClient],
  );

  const retry = useCallback(
    (turn: ChatTurn) => {
      setTurns((current) => current.filter((candidate) => candidate.id !== turn.id));
      void send(turn.text);
    },
    [send],
  );

  const reset = useCallback(() => {
    generation.current += 1;
    setLoading(false);
    setConversationId(null);
    setTurns([]);
    setActive(null);
    setSendError(null);
    setLoadError(false);
    setOverrides({});
    setConflicts(new Set());
    settled.current = null;
  }, []);

  const open = useCallback(
    async (id: string) => {
      reset();
      const mine = generation.current;
      setLoading(true);
      try {
        const detail = await fetchConversation(id);
        if (generation.current !== mine) return;
        setConversationId(detail.id);
        setTurns(turnsOf(detail));
        setLoading(false);
      } catch {
        if (generation.current !== mine) return;
        setLoadError(true);
        setLoading(false);
      }
    },
    [reset],
  );

  const undo = useCallback(
    async (action: AssistantAction) => {
      const summary = actionSummary(t, action);
      setUndoing((current) => new Set(current).add(action.id));
      try {
        const result = await undoAction(action.id, newIdempotencyKey());
        setOverrides((current) => ({ ...current, [action.id]: result }));
        setConflicts((current) => new Set([...current].filter((id) => id !== action.id)));
        toast(t(result.state === 'partially_undone' ? 'assistant.actions.partialToast' : 'assistant.actions.undoneToast', { summary }), result.state === 'partially_undone' ? 'info' : 'success');
      } catch (error) {
        if (error instanceof AssistantRequestError && error.problem === 'assistant-undo-conflict') {
          setConflicts((current) => new Set(current).add(action.id));
          toast(t('assistant.actions.conflictToast', { summary }), 'error');
        } else if (error instanceof AssistantRequestError && error.problem === 'assistant-undo-unavailable') {
          setOverrides((current) => ({ ...current, [action.id]: { ...action, state: 'not_undoable' } }));
          toast(t('assistant.actions.unavailableToast', { summary }), 'error');
        } else toast(t(error instanceof AssistantRequestError && error.status === 0 ? 'assistant.actions.offlineToast' : 'assistant.actions.undoFailedToast'), 'error');
      } finally {
        setUndoing((current) => new Set([...current].filter((id) => id !== action.id)));
      }
    },
    [t, toast],
  );

  return {
    conversationId, turns, active, activeProgress: poll.data?.turn.progress ?? [], sendError, loadError, loading, overrides, conflicts, undoing,
    clearSendError: () => setSendError(null), send, retry, reset, open, undo,
  };
}
